import React, { useCallback, useEffect, useMemo, useState } from 'react'
import Alert from '@mui/material/Alert'
import Typography from '@mui/material/Typography'
import DialogConfirmCancel from '../../../../../dialog/DialogConfirm'
import FormBasicInformation from '../FormBasicInformation/FormBasicInformation'
import usePeriodicalReport from '../../../../../../hooks/periodical-report/usePeriodicalReport'
import { useNavigate } from 'react-router-dom'
import { AlertTitle, FormControl, FormControlLabel, Grid, Radio, RadioGroup } from '@mui/material'
import SelectField from '../../../../../FormControl/input/SelectField'

const DialogAction = ({ show, onClose, type, formData = {} }) => {
  const navigate = useNavigate()
  const types = ['delete', 'clone', 'create', 'edit basic information', 'edit status', 'set as current']

  const [loading, toggleLoading] = useState(false)
  const [errors, setInputErrors] = useState({})
  const [form, setInputForm] = useState(formData)

  const {
    createPeriodicalReport,
    clonePeriodicalReport,
    updatePeriodicalReport,
    setAsCurrentPeriodicalReport,
    deletePeriodicalReport
  } = usePeriodicalReport()

  const onChange = (name, value) => {
    if (name === 'status') {
      let messageStatus = null
      if (value === 'open-for-marking' && form.drsOpen) {
        messageStatus = 'Please note there are open rubrics.'
      }

      setInputForm({ ...form, [name]: value })
      setInputErrors({ ...errors, [name]: null, messageStatus })
    } else {
      setInputForm({ ...form, [name]: value })
      setInputErrors({ ...errors, [name]: null })
    }
  }
  const onConfirm = useCallback(
    async (e) => {
      if (!types.includes(type)) {
        setInputErrors({ message: 'Invalid type' })
        return
      }
      if (type !== 'create' && form._id === undefined) {
        setInputErrors({ message: 'Invalid id' })
        return
      }
      e.preventDefault()
      toggleLoading(true)
      try {
        let result = { error: false }
        if (type === 'create') {
          result = await createPeriodicalReport(form)
        }
        if (type === 'delete') {
          result = await deletePeriodicalReport(form._id)
        }
        if (type === 'clone') {
          result = await clonePeriodicalReport(form._id)
        }
        if (type === 'set as current') {
          result = await setAsCurrentPeriodicalReport(form._id)
        }
        if (type === 'edit status') {
          result = await updatePeriodicalReport(form._id, 'status', { status: form.status })
        }

        if (result.error) {
          setInputErrors(result)
        } else {
          if (type === 'delete') {
            navigate(`/dashboard/periodical-report`)
          }
          onClose()
        }
      } finally {
        toggleLoading(false)
      }
    },
    [form, type]
  )

  useEffect(() => {
    if (type !== 'create') {
      if (!formData._id) {
        setInputErrors({ message: 'Invalid id' })
      }
    }
  }, [formData, type])

  const optionsStatus = useMemo(() => {
    let options = [
      { value: 'draft', label: 'Draft' },
      { value: 'open-for-marking', label: 'Open for marking' }
    ]

    if (formData.status === 'closed') {
      options = [{ value: 'closed', label: 'Closed' }]
    }

    if (formData.status === 'open-for-marking') {
      options = [
        { value: 'draft', label: 'Draft' },
        { value: 'open-for-marking', label: 'Open for marking' },
        { value: 'open-for-review', label: 'Open for review' }
      ]
    }
    if (formData.status === 'open-for-review') {
      options = [
        { value: 'open-for-review', label: 'Open for review' },
        { value: 'closed', label: 'Closed' }
      ]
    }

    return options
  }, [formData.status])

  const checkStatus = useCallback(() => {
    if (form.status === 'open-for-marking' && form.drsOpen && String(form.continueWithDrsOpen) !== 'true') {
      return true
    }
    return false
  }, [form.status, form.continueWithDrsOpen])

  return (
    <DialogConfirmCancel
      id={`periodical-report-${type}-${form?._id}`}
      open={show}
      onCancel={onClose}
      onConfirm={type === 'create' ? onConfirm : types.includes(type) && form._id ? onConfirm : undefined}
      textConfirm={['create', 'edit basic information'].includes(type) ? 'save' : 'Confirm'}
      loading={loading}
      disabledConfirm={checkStatus()}
      title={type}
      onCloseHeader={false}
      maxWidth='xs'
    >
      {['create', 'edit basic information'].includes(type) && (
        <FormBasicInformation form={form} errors={errors} onChange={onChange} type={type} />
      )}
      {['delete', 'clone', 'set as current'].includes(type) && (
        <div>
          <Typography component={'span'}>Are you sure you want to {type} the periodical report? </Typography>
          <b>{form.name}</b>
        </div>
      )}
      {['edit status'].includes(type) && (
        <Grid container spacing={2} alignItems='center'>
          <Grid item md={2} sm={3}>
            <Typography
              variant='inherit'
              className='font-bold capitalize'
              color='primary'
              align='right'
              component='p'
            >
              Status
            </Typography>
          </Grid>
          <Grid item md={10} sm={9}>
            <SelectField
              id='status'
              name='status'
              error={errors.status}
              helperText={errors.status}
              value={form.status}
              options={optionsStatus}
              placeholder='Select a status'
              onChange={(e) => onChange('status', e.target.value)}
            />
          </Grid>
          {errors?.messageStatus && (
            <Grid item sm={12}>
              <Alert severity='warning' className='mb-4'>
                <AlertTitle className='font-bold'>Please note there are open rubrics.</AlertTitle>
                The report will include all the marking completed before this status change. Any marking added
                after the status change will not be reflected in the report.
              </Alert>
              <Typography variant='inherit' color='primary' className='font-bold text-center' component='p'>
                Do you want to proceed?
              </Typography>
              <FormControl className='flex flex-row gap-4 justify-center items-center'>
                <RadioGroup
                  aria-labelledby='demo-radio-buttons-group-label'
                  defaultValue='female'
                  name='radio-buttons-group'
                  value={form.continueWithDrsOpen}
                  className='flex flex-row gap-4 items-center'
                  onChange={(e) => onChange('continueWithDrsOpen', e.target.value === 'yes')}
                >
                  <FormControlLabel value={'yes'} control={<Radio />} label='Yes' />
                  <FormControlLabel value={'no'} control={<Radio />} label='No' />
                </RadioGroup>
              </FormControl>
              <div></div>
            </Grid>
          )}
        </Grid>
      )}
      {errors?.message && (
        <Alert severity='error' className='mt-4'>
          {errors?.message}
        </Alert>
      )}
    </DialogConfirmCancel>
  )
}

export default DialogAction
